import styles from './index.module.scss';
import setResponseHeaders from '@buzzfeed/headers';
import NewsletterForm from '../../components/NewsletterForm';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { useEffect, useRef, useState } from 'react';
import { CaretDownIcon } from '@buzzfeed/react-components';
import { isAndroidAgent } from '@buzzfeed/bf-utils';
import { useTrackingContext } from '../../hooks';

const Page = () => {
  const detailsRef = useRef(null);
  const [screenWidth, setScreenWidth] = useState(0);
  const { trackPageview, trackContentAction } = useTrackingContext();

  useEffect(() => {
    trackPageview();
  }, []);

  useEffect(() => {
    if (isAndroidAgent()) {
      window.location.href = '/android';
    }
  }, []);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setScreenWidth(width);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const onSubmitSuccess = () => {
    trackContentAction({
      action_type: 'signup',
      action_value: 'buzzfeed_email_island',
      item_name: 'submission',
      item_type: 'button',
      position_in_unit: 0,
      subunit_name: '',
      subunit_type: '',
      unit_name: 'main',
      unit_type: 'feed',
    });
  };

  return (
    <div className={styles.container}>
      <main className={styles.content}>
        <Navbar />
        <div className={styles.backgroundImage} />
        <div className={styles.overlay} />
        <div className={styles.contentWrapper}>
          <div className={styles.centeredBox}>
            <div className={styles.innerContentWrapper}>
              <a href="/" className={styles.homeLink}>
                <h1 className={styles.visuallyHidden}>BuzzFeed Island - Join Us</h1>
              </a>
              <div className={styles.textAndMark}>
                <div className={styles.topRow}>
                  <div className={styles.signupPanel} id="signup">
                    <h1 className={styles.heroHeadline}>
                      <span className={styles.heroHeadlineLine1}>Prebook Your Spot on</span>
                      <span className={styles.heroHeadlineLine2}>
                        BF Isl<span className={styles.heroTitleItalic}>a</span>nd.
                      </span>
                    </h1>
                    <p className={styles.waitlistSubhead}>Join the waitlist for exclusive beta access!</p>
                    <NewsletterForm onSubmitSuccess={onSubmitSuccess} />
                  </div>
                  <img
                    className={styles.islandMarkRight}
                    src="/static/images/island-mark.jpg"
                    style={
                      screenWidth >= 833 && screenWidth <= 1288
                        ? { width: '500px', height: 'auto' }
                        : screenWidth >= 641 && screenWidth < 833
                          ? { width: '300px', height: 'auto', objectFit: 'contain', position: 'relative', top: '0', right: '0', alignSelf: 'flex-start' }
                          : screenWidth > 1288
                            ? { width: '755px', height: '755px', position: 'relative', top: '110px', right: '-120px' }
                            : {}
                    }
                    alt="Island mark graphic"
                  />
                </div>
              </div>
            </div>
            <div className={styles.bottomLink}>
              <button
                type="button"
                className={styles.learnMoreLink}
                onClick={() => {
                  if (detailsRef.current) {
                    const el = detailsRef.current;
                    const y = el.getBoundingClientRect().top + window.pageYOffset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                  }
                }}
                aria-controls="learn-more-details"
              >
                <span>Learn more</span>
                <CaretDownIcon
                  className={styles.learnMoreArrow}
                />
              </button>
            </div>
          </div>
          <div id="learn-more-details" ref={detailsRef} className={styles.learnMoreDetails} role="region" aria-live="polite">
            <div className={styles.learnMoreTextBlock}>
              <p>Get ready to explore a whole new world of endless creative possibilities.</p>
              <p>BF Island is a community built around creativity, chaos, and unfiltered joy. Soon, you’ll be able to:</p>
            </div>
            <div className={styles.learnMoreRowsContainer}>
              <div className={styles.learnMoreItemsRow}>
                <div className={styles.learnMoreItem}>
                  <div className={styles.buildIcon} aria-hidden="true" />
                  <div className={styles.buildLabel}>Dream up</div>
                  <div className={styles.learnMoreDescription}>
                    images from thin air, then share them with close friends and confused strangers.
                  </div>
                </div>
                <div className={`${styles.learnMoreItem} ${styles.learnMoreItemCompete}`}>
                  <div className={styles.competeIcon} aria-hidden="true" />
                  <div className={styles.competeLabel}>Compete</div>
                  <div className={styles.learnMoreDescription}>
                    in themed contests for bragging rights and internet points.
                  </div>
                </div>
                <div className={`${styles.learnMoreItem} ${styles.learnMoreItemCraft}`}>
                  <div className={styles.craftIcon} aria-hidden="true" />
                  <div className={styles.craftLabel}>Craft</div>
                  <div className={`${styles.learnMoreDescription} ${styles.learnMoreDescriptionShort}`}>
                    custom memes from scratch – pure unfiltered genius
                  </div>
                </div>
              </div>
              <div className={styles.learnMoreItemsRowSecondary}>
                <div className={styles.learnMoreSecondaryItem}>
                  <div className={styles.learnMoreSecondaryItemInner}>
                    <div className={styles.voteIcon} aria-hidden="true" />
                    <div className={styles.voteLabel}>Vote (or rant)</div>
                    <div className={styles.voteDescription}>
                      in community polls. Expect hot takes.
                    </div>
                  </div>
                </div>
                <div className={styles.learnMoreSecondaryItem}>
                  <div className={styles.learnMoreSecondaryItemInner}>
                    <div className={styles.diyIcon} aria-hidden="true" />
                    <div className={styles.doLabel}>Explore</div>
                    <div className={styles.doDescription}>
                      weird, wonderful tools we’re still cooking up.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.learnMoreRowsContainerMobile} aria-hidden="true">
              <div className={styles.mobileRowBuildCompete}>
                <div className={styles.mobileItemBuild}>
                  <div className={styles.buildIcon} aria-hidden="true" />
                  <div className={styles.buildLabel}>Dream up</div>
                  <div className={styles.learnMoreDescription}>
                    images from thin air, then share them with close friends and confused strangers.
                  </div>
                </div>
                <div className={styles.mobileItemCompete}>
                  <div className={styles.competeIcon} aria-hidden="true" />
                  <div className={styles.competeLabel}>Compete</div>
                  <div className={styles.learnMoreDescription}>
                    in themed contests for bragging rights and internet points.
                  </div>
                </div>
              </div>
              <div className={styles.mobileRowVoteCraft}>
                <div className={styles.mobileItemVote}>
                  <div className={styles.voteIcon} aria-hidden="true" />
                  <div className={styles.voteLabel}>Vote (or rant)</div>
                  <div className={styles.voteDescription}>
                    in community polls. Expect hot takes.
                  </div>
                </div>
                <div className={styles.mobileItemCraft}>
                  <div className={styles.craftIcon} aria-hidden="true" />
                  <div className={styles.craftLabel}>Craft</div>
                  <div className={`${styles.learnMoreDescription} ${styles.learnMoreDescriptionShort}`}>
                    custom memes from scratch – pure unfiltered genius
                  </div>
                </div>
              </div>
              <div className={styles.mobileRowDo}>
                <div className={styles.mobileItemDo}>
                  <div className={styles.diyIcon} aria-hidden="true" />
                  <div className={styles.doLabel}>Explore</div>
                  <div className={styles.doDescription}>
                    weird, wonderful tools we’re still cooking up.
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.afterVoteDoPanel}>
              <div className={styles.afterVoteDoPanelText}>
                <p>Spots are limited.<br />Be among the first to set foot on the Island.</p>
                <p>Let’s have some fun with our dang phones.</p>
              </div>
              <a
                href="#top"
                className={styles.afterVoteDoPanelCta}
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Sign Up Now
              </a>
            </div>
            <Footer />
            <div className={styles.learnMoreDetailsContent}>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Page;

export async function getServerSideProps({ req, res }) {
  try {
    setResponseHeaders({
      req, res, maxAge: 86400, contentIDs: []
    });

    return {
      props: {
        tracking: { context_page_id: 'home', context_page_type: 'utility' }
      },
    };
  } catch (error) {
    if (error.status === 404) {
      return {
        notFound: true,
      };
    }
    throw error;
  }
}
